import { Form, FormItemProps, Select, SelectProps } from 'antd';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { getOptionsWithSelectAll } from './utils/get-options-with-select-all';
import { useCheckValueExists } from './hooks/use-check-value-exists';

export interface AntSelectOption<LabelType extends string | ReactNode = string | ReactNode> {
  readonly label: LabelType;
  readonly value: any;
}

export type OnSelectChange = (values: any, option?: AntSelectOption | AntSelectOption[]) => void;

interface Props extends SelectProps<any, AntSelectOption> {
  readonly errorMessage?: string;
  readonly hasSelectAllOption?: boolean;
  readonly formItemProps?: FormItemProps;
}

const AntSelectWrapper = ({
  // workaround for using without form to prevent clearing value by Form.Item
  internalValue,
  value = internalValue,
  placeholder,
  onChange,
  options = [],
  hasSelectAllOption,
  ...props
}: Props & { internalValue?: any }) => {
  const { t } = useTranslation();

  const existingValue = useCheckValueExists(value, options, onChange);
  const [overriddenOptions, overriddenOnChange] =
    hasSelectAllOption && props.mode === 'multiple'
      ? getOptionsWithSelectAll(options || [], t('selectAll'), onChange)
      : [options, onChange];

  return (
    <Select
      showSearch
      allowClear
      maxTagCount="responsive"
      optionFilterProp="label"
      {...props}
      placeholder={placeholder || t('select')}
      value={existingValue}
      options={overriddenOptions}
      onChange={overriddenOnChange}
    />
  );
};

export const AntSelect = ({ style, id, errorMessage, formItemProps, ...props }: Props) => {
  return (
    <Form.Item
      {...formItemProps}
      style={style}
      htmlFor={id}
      help={errorMessage}
      validateStatus={errorMessage ? 'error' : undefined}
    >
      <AntSelectWrapper id={id} internalValue={props.value} {...props} />
    </Form.Item>
  );
};
